import {
  type FieldValue,
  type Firestore,
  deleteField,
  doc,
  setDoc,
  updateDoc,
} from "firebase/firestore";

/**
 * WriteMap uses slash-delimited RTDB-style paths as keys. For Firestore we
 * split each key into (docPath, fieldPath) and group writes by doc, then call
 * updateDoc once per doc with dot-delimited field paths.
 *
 * Doc boundaries recognised:
 *   tables/{tableId}
 *   tables/{tableId}/characters/{charId}
 *   tables/{tableId}/scenes/{sceneId}
 *   tables/{tableId}/compels/{compelId}
 *   tables/{tableId}/presence/{uid}
 *   tables/{tableId}/log/{entryId}
 *
 * Paths with leading "/" are tolerated.
 */

interface Parsed {
  docPath: string;
  fieldPath: string | null;
}

const SUBCOLLS = ["characters", "scenes", "compels", "presence", "log"] as const;

function parseKey(rawKey: string): Parsed {
  const key = rawKey.replace(/^\/+/, "");
  const parts = key.split("/");
  if (parts[0] !== "tables" || parts.length < 2) {
    throw new Error(`writes: unsupported path "${rawKey}"`);
  }
  // Determine doc boundary depth: 2 (tables/{id}) or 4 (tables/{id}/sub/{id}).
  let docDepth = 2;
  if (parts.length >= 4 && (SUBCOLLS as readonly string[]).includes(parts[2] ?? "")) {
    docDepth = 4;
  }
  const docPath = parts.slice(0, docDepth).join("/");
  const fieldPath = parts.length > docDepth ? parts.slice(docDepth).join(".") : null;
  return { docPath, fieldPath };
}

/**
 * Apply an RTDB-shaped multi-path WriteMap against Firestore. Entries sharing
 * a doc are merged into a single updateDoc call with dot-path field keys.
 * Nulls map to deleteField() sentinels. Entries targeting a whole doc (no
 * trailing field) are issued as setDoc with merge.
 */
export async function applyWriteMap(db: Firestore, writes: Record<string, unknown>): Promise<void> {
  const docUpdates = new Map<string, Record<string, unknown | FieldValue>>();
  const docSets: Array<{ path: string; value: Record<string, unknown> }> = [];

  for (const [rawKey, value] of Object.entries(writes)) {
    const { docPath, fieldPath } = parseKey(rawKey);
    if (fieldPath === null) {
      // Whole-doc write. null means "delete" but Firestore has no one-shot
      // delete here; callers use deleteDoc directly when that's the intent.
      if (value === null) {
        throw new Error(`writes: refusing to null whole doc at "${rawKey}"`);
      }
      docSets.push({ path: docPath, value: value as Record<string, unknown> });
      continue;
    }
    const bucket = docUpdates.get(docPath) ?? {};
    bucket[fieldPath] = value === null ? deleteField() : value;
    docUpdates.set(docPath, bucket);
  }

  const ops: Array<Promise<unknown>> = [];
  for (const { path, value } of docSets) {
    ops.push(setDoc(doc(db, path), value, { merge: true }));
  }
  for (const [path, fields] of docUpdates) {
    // updateDoc's generic signature requires a FieldValue-compatible shape;
    // our map already contains plain JSON or deleteField() sentinels.
    ops.push(updateDoc(doc(db, path), fields as { [k: string]: FieldValue }));
  }
  await Promise.all(ops);
}
